import React, { useEffect, useState } from 'react';
import MainLayout from '../../layout/main-layout';
import { useSelector } from 'react-redux';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';
import DuoStatCard from '../../components/ui/duo-stat-card';
import { APP_API_URL } from '../../utils';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { formatDate } from '../../utils/functions';

const periods = [
  { label: "Aujourd'hui", value: "aujourd'hui" },
  { label: 'Hier', value: 'hier' },
  { label: '7 jours', value: 'dernier_7_jours' },
  { label: '30 jours', value: 'dernier_30_jours' },
  { label: 'Ce mois', value: 'ce_mois' },
  { label: 'Mois dernier', value: 'dernier_mois' },
];

const QUICK_ACTIONS = [
  { to: '/admin/sales-broken-tiles', label: 'Carreaux cassés', icon: 'bi bi-grid-3x3-gap', tone: 'green' },
  { to: '/admin/damaged-goods/sales', label: 'Articles cassés', icon: 'bi bi-bandaid', tone: 'orange' },
  { to: '/admin/dons/nouveau', label: 'Don', icon: 'bi bi-gift', tone: 'purple' },
  { to: '/clients', label: 'Clients', icon: 'bi bi-people', tone: 'blue' },
  { to: '/articles', label: 'Produits', icon: 'bi bi-box', tone: 'orange' },
  { to: '/commandes', label: 'Ventes', icon: 'bi bi-cart-check', tone: 'purple' },
];

const statusBadgeClass = (statut) => {
  if (statut === 'en cours') return 'duo-badge duo-badge--warning';
  if (statut === 'approuvé') return 'duo-badge duo-badge--info';
  if (statut === 'livré') return 'duo-badge duo-badge--success';
  return 'duo-badge';
};

const Welcome = () => {
  const user = useSelector((state) => state.auth.user);
  const [selectedPeriod, setSelectedPeriod] = useState(periods[0]);
  const [lastData, setLastData] = useState([]);
  const [salesData, setSalesData] = useState(null);
  const [weeklyRevenue, setWeeklyRevenue] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingLast, setLoadingLast] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');

    axios.get(`${APP_API_URL}/orders/last-delivered`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      if (!cancelled) setLastData(response.data.commandes ?? []);
    }).catch((error) => {
      console.error('Erreur lors de la récupération des commandes :', error);
      if (!cancelled) setLastData([]);
    }).finally(() => {
      if (!cancelled) setLoadingLast(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoadingStats(true);

    Promise.all([
      axios.post(
        `${APP_API_URL}/sales/stats`,
        { periode: selectedPeriod.value, user_id: user.id },
        { headers }
      ),
      axios.post(
        `${APP_API_URL}/sales/weekly-revenue`,
        { periode: selectedPeriod.value },
        { headers }
      ),
    ]).then(([statsResponse, revenueResponse]) => {
      if (cancelled) return;
      setSalesData(statsResponse.data);
      setWeeklyRevenue(revenueResponse.data);
    }).catch((error) => {
      console.error('Erreur lors de la récupération du tableau de bord :', error);
    }).finally(() => {
      if (!cancelled) setLoadingStats(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedPeriod.value, user?.id]);

  const firstName = user?.name?.split(' ')[0] ?? 'équipe';

  return (
    <MainLayout>
      <div className="duo-page py-3 px-1 px-md-2">
        <div className="duo-hero mb-4">
          <p className="duo-hero-sub mb-1">Tableau de bord ventes</p>
          <h1 className="duo-hero-title">
            Salut {firstName} !
          </h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Voici l&apos;activité de votre magasin — {selectedPeriod.label.toLowerCase()}.
          </p>
        </div>

        <div className="mb-4">
          <DuoPeriodPicker
            options={periods}
            selectedOption={selectedPeriod}
            onSelect={setSelectedPeriod}
          />
        </div>

        <div className="row g-3 mb-4">
          <div className="col-md-6 col-lg-4">
            <DuoStatCard
              variant="green"
              icon="bi bi-currency-exchange"
              label={`Chiffre d'affaires · ${selectedPeriod.label}`}
              value={weeklyRevenue?.revenu}
              meta={weeklyRevenue?.changement ? `↗ ${weeklyRevenue.changement}` : null}
              loading={loadingStats && !weeklyRevenue}
            />
          </div>
          <div className="col-md-6 col-lg-4">
            <DuoStatCard
              variant="blue"
              icon="bi bi-receipt"
              label="Ventes totales"
              value={salesData?.total_ventes}
              loading={loadingStats && !salesData}
            />
          </div>
          <div className="col-md-6 col-lg-4">
            <DuoStatCard
              variant="orange"
              icon="bi bi-hourglass-split"
              label="Commandes en cours"
              value={salesData?.ventes_en_cours}
              loading={loadingStats && !salesData}
            />
          </div>
        </div>

        <section className="mb-4">
          <h2 className="fw-bold text-duo-text mb-3" style={{ color: '#3c3c3c', fontSize: '1rem' }}>
            Actions rapides
          </h2>
          <div className="duo-action-grid">
            {QUICK_ACTIONS.map((action) => (
              <Link
                key={action.label}
                to={action.to}
                className={`duo-action-btn duo-action-btn--${action.tone}`}
              >
                <span className="duo-action-btn__icon">
                  <i className={action.icon} aria-hidden />
                </span>
                {action.label}
              </Link>
            ))}
          </div>
        </section>

        <section className="duo-panel">
          <div className="duo-panel__head">
            <h2 className="duo-panel__title">Transactions récentes</h2>
            {!loadingLast && (
              <span className="duo-badge duo-badge--info">{lastData.length} vente{lastData.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {loadingLast ? (
            <p className="duo-empty">Chargement des ventes…</p>
          ) : lastData.length > 0 ? (
            <ul className="duo-tx-list">
              {lastData.map((item, index) => {
                const ref = item.commande?.designation ?? '—';
                const shortRef = ref.length > 6 ? ref.slice(-6) : ref;
                return (
                  <li key={`tx-${index}`} className="duo-tx-item">
                    <div className="duo-tx-ref" title={ref}>
                      {shortRef}
                    </div>
                    <div className="duo-tx-body">
                      <p className="duo-tx-client">
                        {item?.commande?.customer?.name ?? 'Client inconnu'}
                      </p>
                      <p className="duo-tx-date">
                        {formatDate(item?.commande?.created_at)}
                      </p>
                    </div>
                    <span className={statusBadgeClass(item.commande?.statut_commande)}>
                      {item.commande?.statut_commande ?? '—'}
                    </span>
                    <Link
                      to={`/commande/${item.commande?.designation}`}
                      className="duo-btn-view"
                    >
                      <i className="bi bi-eye" aria-hidden />
                      Voir
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="duo-empty">
              Aucune transaction récente pour le moment.
              <br />
              <Link to="/commandes" className="duo-btn-view mt-3 d-inline-flex">
                Ouvrir la caisse
              </Link>
            </p>
          )}
        </section>
      </div>
    </MainLayout>
  );
};

export default Welcome;
